import { createContext, useState, type ReactNode, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventService } from '../../../shared/services/tenant/eventService';
import { useAuth } from '../../auth';
import { UserRole } from '../../../utils/userMapper';


export interface DynamicField {
  label: string;
  type: 'string' | 'number' | 'boolean' | 'date';
  required: boolean;
  placeholder?: string;
}

export interface Event {
  id?: string;
  title: string;
  start_date: string;
  end_date: string;
  start_time?: string | null;
  end_time?: string | null;
  overnight?: boolean | null;
  description?: string | null;
  observation?: string | null;
  image?: string | null;
  rules_for_children?: string | null;
  own_transport?: boolean | null;
  valor_ingresso_inteiro: number;
  valor_ingresso_meia: number;
  valor_ingresso_estudante: number;
  valor_ingresso_crianca: number;
  organizer_id?: string;
  secondary_organizer_ids?: string[];
  dynamicFields?: DynamicField[];
  organizer_name?: string;
  created_at?: string;
  status: 'open' | 'closed' | 'cancelled';
  registration_deadline: string | null;
  phone?: string;
  role?: string;
  label_ingresso_meia?: string;
  label_ingresso_inteiro?: string;
  label_ingresso_estudante?: string;
  label_ingresso_crianca?: string;
  description_ingresso_inteiro?: string;
  description_ingresso_meia?: string;
  description_ingresso_estudante?: string;
  description_ingresso_crianca?: string;
  souvenirs?: {
    id?: string;
    title: string;
    description?: string;
    price: number;
    image?: string;
  }[];
  video_url?: string;
  organizer?: {
    id: string;
    name: string;
    email: string;
    phone?: string;
    cpf_or_rg?: string;
    birth_date?: string;
    role?: string;
  };
  // Métodos de pagamento
  payment_pix?: boolean;
  payment_card?: boolean;
  payment_boleto?: boolean;
  // Taxas e juros
  boleto_interest?: number;
  boleto_fine?: number;
  card_fee?: number;
  card_fee_percent?: number;
  show_organizer_email?: boolean;
  show_organizer_phone?: boolean;
  registration_title?: string; // Título customizado para a seção de inscrição
  registration_subtitle?: string;
  gtm_container_id?: string;
}

interface EventFilters {
  title?: string;
  status?: string;
  hasTickets?: boolean;
  archived?: boolean;
}

interface EventContextData {
  events: Event[];
  isLoading: boolean;
  error: string | null;
  createEvent: (data: Omit<Event, 'id'>) => Promise<void>;
  updateEvent: (id: string, data: Partial<Event>) => Promise<void>;
  getEventById: (id: string) => Event | undefined;
  removeEvent: (id: string) => Promise<void>;
  eventData: Event | null;
  isDataLoading: boolean;
  setError: (error: string | null) => void;
  fetchEvents: () => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  page: number;
  limit: number;
  total: number;
  filters: EventFilters;
  setFilters: (filters: EventFilters) => void;
  clearFilters: () => void;
}

export const EventContext = createContext<EventContextData>({} as EventContextData);

export function EventProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);

  // PAGINAÇÃO
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);

  // FILTROS
  const [filters, setFilters] = useState<EventFilters>({});

  // Reset page quando mudar limit ou filtros
  useEffect(() => {
    setPage(1);
  }, [limit, filters]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  // Query de eventos paginados
  const {
    data: response = { events: [], total: 0 },
    isLoading,
    refetch: refetchEvents
  } = useQuery({
    queryKey: ['events', user?.role, page, limit, filters],
    queryFn: async () => {
      try {
    
        const res = user?.role === UserRole.ORGANIZER
          ? await eventService.listPerOrganizer(page, limit)
          : await eventService.list(page, limit, filters);

          return user?.role === UserRole.ORGANIZER ? res : res.data || { events: [], total: 0 };
      } catch (error: any) {
        setError(error.response?.data?.message || error.message || 'Erro ao buscar eventos');
        return { events: [], total: 0 };
      }
    },
    enabled: true,
  });

  const fetchEvents = () => refetchEvents();

  const { data: eventData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        return await eventService.get(id).then(res => res.data);
      } catch (error: any) {
        setError(error.response?.data?.message || error.message || 'Erro ao buscar evento');
        return null;
      }
    },
    enabled: !!id,
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Event, 'id'>) => eventService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || error.message || 'Erro ao criar evento');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Event> }) =>
      eventService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event', id] }); 
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || error.message || 'Erro ao atualizar evento');
    }
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => eventService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      navigate('/events');
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || error.message || 'Erro ao excluir evento');
    }
  });

  const createEvent = async (data: Omit<Event, 'id'>) => {
    const result = await createMutation.mutateAsync(data);
    return result.data;
  };


  const updateEvent = async (id: string, data: Partial<Event>) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const removeEvent = async (id: string) => {
    await removeMutation.mutateAsync(id);
  };

  const getEventById = (id: string) => response.data?.find((e: any) => e.id === id);

  const handleSetFilters = (newFilters: EventFilters) => {
    setFilters(newFilters);
  };

  const clearFilters = () => {
    const currentArchived = filters.archived;
    setFilters(currentArchived !== undefined ? { archived: currentArchived } : {});
  };

  return (
    <EventContext.Provider
      value={{
        events: response.data,
        isLoading,
        error,
        createEvent,
        updateEvent,
        getEventById,
        removeEvent,
        eventData,
        isDataLoading,
        setError,
        fetchEvents,
        page: response.page ?? page,
        limit: response.limit ?? limit,
        total: response.total,
        setPage: handlePageChange,
        setLimit: handleLimitChange,
        filters,
        setFilters: handleSetFilters,
        clearFilters,
      }}
    >
      {children}
    </EventContext.Provider>
  );
}